import { FUEL, type OwnedItem } from '@accra/shared';
import { readJson, writeJson } from '../core/storage/local';
import { DEFAULT_EQUIPPED, type Equipped } from './garage';
import { newBoard, type MissionBoard } from './missions';
import type { Streak } from './streak';
import type { PlayerVehicleId } from '../content/playerVehicles';
import type { TrafficLevel } from '../content/trafficLevels';

/** The player's local save (the server's wallet and owned items win when online). */
const KEY = 'trotrorun.profile.v1';

export interface Profile {
  version: 1;
  bestScore: number;
  /** Money the driver has; negative means they owe (fines and repairs). */
  walletPesewas: number;
  runs: number;
  vehicle: PlayerVehicleId;
  routeId: string;
  /** Mirror of the server's owned items (the server is the source of truth). */
  owned: OwnedItem[];
  unlocks: string[];
  equipped: Equipped;
  missions: MissionBoard;
  streak: Streak;
  name: string | null;
  traffic: TrafficLevel;
  /** Petrol left in the tank after the last ordinary shift (units, 0–100). It carries over to the next one. */
  fuelUnits: number;
}

const DEFAULT_PROFILE: Profile = { version: 1, bestScore: 0, walletPesewas: 0, runs: 0, vehicle: 'sprinter', routeId: 'madina-accra',
  owned: [], unlocks: [], equipped: DEFAULT_EQUIPPED, missions: newBoard(1), streak: { count: 0, lastDay: null, graceUsed: null }, name: null, traffic: 'normal', fuelUnits: FUEL.tankUnits,
};

/** The tank a normal shift begins with: what you had left, but never under the minimum (and a new save starts full). */
export function carriedFuel(units: number | undefined): number {
  if (units === undefined || !Number.isFinite(units)) return FUEL.tankUnits;
  return Math.min(FUEL.tankUnits, Math.max(FUEL.minStartUnits, units));
}

export function loadProfile(): Profile {
  const parsed = readJson<Partial<Profile> & { bankPesewas?: number }>(KEY);
  if (!parsed) return { ...DEFAULT_PROFILE };
  // Older saves called the wallet "bank".
  const walletPesewas = parsed.walletPesewas ?? parsed.bankPesewas ?? 0;
  return { ...DEFAULT_PROFILE, ...parsed, equipped: { ...DEFAULT_EQUIPPED, ...parsed.equipped }, walletPesewas, version: 1 };
}

export function saveProfile(profile: Profile): void {
  writeJson(KEY, profile);
}
